<template>
  <dx-modal
    :value="true"
    :closable="false"
    id="terms-acceptance-modal"
    class="terms-acceptance-modal"
  >
    <template #header>
      <div class="terms-acceptance-header">
        <div class="terms-acceptance-icon">
          <fa-icon :icon="['fal', 'file-signature']" />
        </div>
        <div class="terms-acceptance-heading">
          <div class="terms-acceptance-eyebrow">Antes de continuar</div>
          <div class="terms-acceptance-title">Termos de uso do Stenci</div>
          <div class="terms-acceptance-subtitle">
            Como responsável pela conta, você precisa ler e aceitar os termos de uso
            para seguir usando o sistema. O aceite é registrado uma única vez e fica
            disponível em Conta do cliente.
          </div>
        </div>
        <div class="terms-acceptance-progress-label">
          <span class="terms-acceptance-progress-value">{{ progressPercent }}%</span>
          <span>lido</span>
        </div>
      </div>
      <div class="terms-acceptance-progress">
        <div class="terms-acceptance-progress-bar" :style="{ width: `${progressPercent}%` }" />
      </div>
    </template>

    <div class="terms-acceptance-layout">
      <div class="terms-acceptance-document">
        <div class="terms-acceptance-box" ref="box" @scroll="onScroll">
          <terms-content />
        </div>
        <transition name="fade">
          <button
            v-if="!reachedEnd"
            type="button"
            class="btn btn-sm terms-acceptance-jump"
            @click="scrollToEnd"
          >
            <fa-icon :icon="['fal', 'arrow-down']" />
            Ir para o final
          </button>
        </transition>
      </div>
    </div>

    <div class="terms-acceptance-consent" :class="{ 'is-ready': reachedEnd }">
      <label class="form-checkbox">
        <input type="checkbox" v-model="agreed" :disabled="!reachedEnd || accepting">
        <i class="form-icon"></i>
        Li e concordo com os Termos de uso e com a
        <a href="/privacy-policy" target="_blank" class="text-primary text-bold">
          Política de Privacidade
        </a>
      </label>
      <div class="terms-acceptance-consent-hint" v-if="!reachedEnd">
        <fa-icon :icon="['fal', 'info-circle']" />
        Role o documento até o final para liberar o aceite.
      </div>
    </div>

    <template #footer>
      <div class="terms-acceptance-footer">
        <div class="terms-acceptance-meta">
          <div>Última atualização em <strong>{{ version | date }}</strong></div>
          <div v-if="user">Aceite registrado em nome de <strong>{{ user.name }}</strong></div>
        </div>
        <div class="terms-acceptance-actions">
          <router-link to="/logout" class="btn">Sair</router-link>
          <button
            type="button"
            class="btn btn-primary btn-icon btn-icon-left ml-2"
            :class="{ loading: accepting }"
            :disabled="!agreed || accepting"
            @click="accept"
          >
            <fa-icon :icon="['fal', 'check-circle']" />
            Aceitar e continuar
          </button>
        </div>
      </div>
    </template>
  </dx-modal>
</template>

<script>
import { mapState } from 'vuex';
import TermsContent from './TermsContent.vue';

const END_THRESHOLD = 24;

export default {
  name: 'TermsAcceptanceModal',
  components: {
    TermsContent,
  },
  data() {
    return {
      agreed: false,
      accepting: false,
      reachedEnd: false,
      progress: 0,
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    version() {
      return this.user?.terms?.version || null;
    },
    progressPercent() {
      return Math.round(this.progress * 100);
    },
  },
  mounted() {
    this.$nextTick(() => this.onScroll());
  },
  methods: {
    onScroll() {
      const { box } = this.$refs;
      if (!box) return;

      const scrollable = box.scrollHeight - box.clientHeight;
      this.progress = scrollable <= 0 ? 1 : Math.min(1, box.scrollTop / scrollable);

      if (scrollable - box.scrollTop <= END_THRESHOLD) {
        this.reachedEnd = true;
      }
    },
    scrollToEnd() {
      const { box } = this.$refs;
      if (!box) return;
      box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
    },
    async accept() {
      if (!this.agreed || this.accepting) return;
      this.accepting = true;
      try {
        await this.$store.dispatch('acceptTerms');
        this.$toast.success('Termos de uso aceitos.');
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.accepting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';
@import '@/assets/scss/_mixins.scss';

.terms-acceptance-modal {
  .modal .modal-container {
    height: 92vh;
    max-height: 92vh;
    max-width: calc(100vw - 2rem);
    width: 48rem;
  }
  .modal-header {
    border-bottom: $border-width solid $border-color;
    padding-bottom: 0;
  }
  .modal-body {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    padding-bottom: $unit-6;
  }
  .modal-footer {
    border-top: $border-width solid $border-color;
    padding-bottom: $unit-5;
    padding-top: $unit-5;
  }
  .terms-acceptance-header {
    align-items: flex-start;
    display: flex;
    gap: $unit-4;
    padding-bottom: $unit-4;
  }
  .terms-acceptance-heading {
    flex: 1;
    min-width: 0;
  }
  .terms-acceptance-icon {
    align-items: center;
    background-color: lighten($primary-color, 34%);
    border-radius: 50%;
    color: $primary-color;
    display: flex;
    flex: 0 0 2.2rem;
    font-size: $unit-6;
    height: 2.2rem;
    justify-content: center;
    width: 2.2rem;
  }
  .terms-acceptance-eyebrow {
    color: $primary-color;
    font-size: $font-size-xs;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
  }
  .terms-acceptance-title {
    color: $gray-color-900;
    font-size: $unit-6;
    font-weight: 700;
    line-height: 1.3;
  }
  .terms-acceptance-subtitle {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-top: $unit-1;
    max-width: 40rem;
  }
  .terms-acceptance-progress-label {
    align-self: center;
    color: $gray-color-dark;
    font-size: $font-size-xs;
    text-align: right;
    white-space: nowrap;
    .terms-acceptance-progress-value {
      color: $gray-color-900;
      display: block;
      font-size: $unit-6;
      font-variant-numeric: tabular-nums;
      font-weight: 700;
      line-height: 1;
    }
  }
  .terms-acceptance-progress {
    background-color: $gray-color-ultra-light;
    height: 3px;
    margin: 0 (-$unit-4);
    overflow: hidden;
    .terms-acceptance-progress-bar {
      background-color: $primary-color;
      height: 100%;
      transition: width .15s ease-out;
    }
  }
  .terms-acceptance-layout {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    padding-top: $unit-4;
  }
  .terms-acceptance-document {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    position: relative;
  }
  .terms-acceptance-box {
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: $unit-6 $unit-8;
    position: relative;
    @include scroll-bar();
    .terms-content {
      font-size: .72rem;
      .terms-content-body {
        margin-top: $unit-6;
      }
      .page-subtitle {
        margin-top: $unit-8;
      }
    }
  }
  .terms-acceptance-jump {
    background-color: $gray-color-900;
    border-color: $gray-color-900;
    border-radius: 1rem;
    bottom: $unit-4;
    box-shadow: 0 .15rem .4rem rgba(0, 0, 0, .25);
    color: $light-color;
    left: 50%;
    padding: 0 $unit-4;
    position: absolute;
    transform: translateX(-50%);
    z-index: 1;
    svg {
      margin-right: $unit-1;
    }
    &:hover, &:focus {
      background-color: lighten($gray-color-900, 8%);
      box-shadow: 0 .15rem .4rem rgba(0, 0, 0, .25);
      color: $light-color;
    }
  }
  .terms-acceptance-consent {
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    gap: $unit-2 $unit-6;
    margin: $unit-3 0 $unit-4;
    .form-checkbox {
      color: $gray-color-dark;
      font-size: .75rem;
      font-weight: 600;
      line-height: 1.4;
      margin: 0;
      padding-left: 1.4rem;
      transition: color .15s;
      .form-icon {
        height: .9rem;
        top: .25rem;
        width: .9rem;
      }
    }
    &.is-ready .form-checkbox {
      color: $gray-color-900;
    }
    .terms-acceptance-consent-hint {
      color: $warning-color;
      font-size: $font-size-sm;
      svg {
        margin-right: $unit-1;
      }
    }
  }
  .terms-acceptance-footer {
    align-items: center;
    display: flex;
    gap: $unit-4;
    justify-content: space-between;
  }
  .terms-acceptance-meta {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    line-height: 1.5;
    text-align: left;
    strong {
      color: $gray-color-900;
    }
  }
  .terms-acceptance-actions {
    white-space: nowrap;
  }
  .fade-enter-active, .fade-leave-active {
    transition: opacity .2s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  @media (max-width: 960px) {
    .terms-acceptance-progress-label, .terms-acceptance-meta {
      display: none;
    }
    .terms-acceptance-box {
      padding: $unit-4;
    }
  }
}
</style>
